import { Calendar, Check, Clock, ImageSquare, LinkSimple, MapPin, MapTrifold, UsersThree, X } from '@phosphor-icons/react';
import {
  RESERVEES_AUX_PRIVES,
  type CreateEventInput,
  type EventCategory,
  type LangueParlee,
} from '@sirocco/shared';
import { lazy, Suspense, useEffect, useId, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { SiroccoApiError } from '../../api/client';
import { openPicker } from '../../utils/picker';
import { events } from '../../api/endpoints';
import { SubScreen } from '../../components/layout/Screens';
import { Button } from '../../components/ui/Button';
import { ChampDesLangues } from '../../components/ui/ChampDesLangues';
import { Field, TextArea, Toggle } from '../../components/ui/Field';
import { PhotoPicker } from '../../components/ui/PhotoPicker';
import { chercherUneAdresse, type Adresse } from '../../utils/adresses';
import { todayIso } from '../../utils/format';
import { useSession } from '../../store/SessionContext';
import { maquetteExiste } from './maquette/donnees';
import { avecLesLieuxDonnes, chercherSurLeCampus, lieuxDuCampus, type LieuDuCampus } from './maquette/lieux';
import { langue, t } from '../../i18n';

/* Le plan du campus, et sa bibliotheque de carte : charges seulement la
   ou l'on s'en sert, pas au demarrage de l'application. */
const PlacerSurLeCampus = lazy(() =>
  import('./carte/PlacerSurLeCampus').then((module) => ({ default: module.PlacerSurLeCampus })),
);
/* Sa maquette, quand le campus en a une : on y place la sortie en 3D. */
const PlacerSurLaMaquette = lazy(() =>
  import('./maquette/PlacerSurLaMaquette').then((module) => ({ default: module.PlacerSurLaMaquette })),
);

/** Ordre des pastilles, celui des maquettes. */
const CATEGORIES: EventCategory[] = [
  'sport',
  'culture',
  'pro',
  'food',
  'asso',
  'etudes',
  'jeux',
];

/** En dessous de deux personnes, ce n'est plus un evenement. */
const MIN_PARTICIPANTS = 2;

/** Le lieu d'ou l'on vient, depuis l'onglet Lieux : l'evenement s'y tiendra. */
export interface LieuPropose {
  libelle: string;
  lat: number;
  lon: number;
  categorie?: EventCategory;
}

/**
 * Creation d'un evenement.
 *
 * Le bouton de publication vit dans la barre du bas, hors du defilement :
 * on declenche donc l'envoi au clic plutot qu'a la soumission d'un
 * formulaire, qui ne saurait pas l'atteindre.
 */
export function CreateEventScreen() {
  const navigate = useNavigate();
  const { server, ambiance } = useSession();
  const propose = (useLocation().state as { lieu?: LieuPropose } | null)?.lieu;
  /*
    Un evenement d'association, cree depuis sa page : il y parait, et dans
    l'onglet des evenements avec son nom. Les regles exigent qu'on en soit
    membre.
  */
  const [parametres] = useSearchParams();
  const assoDemandee = parametres.get('asso');
  const asso = assoDemandee && server?.associations?.[assoDemandee] ? assoDemandee : null;
  const nomDeLAsso = asso ? server?.associations?.[asso]?.nom : undefined;
  /*
    Sur un campus, le lieu ne se cherche pas dans l'annuaire.

    Toutes les sorties de l'ESSEC ont la meme adresse : elles tombaient au
    meme point de la carte. On y ecrit donc le nom du lieu tel qu'on le dit
    — « Amphi Blaise Pascal » — et on le pose au doigt sur le plan.
  */
  const campus = server?.campus === true && server.centre ? server.centre : null;
  /* Hors d'un serveur prive, pas de categorie d'ecole : voir `RESERVEES_AUX_PRIVES`. */
  const categories = server?.prive
    ? CATEGORIES
    : CATEGORIES.filter((value) => !RESERVEES_AUX_PRIVES.includes(value));
  const [placer, setPlacer] = useState(false);
  /*
    Les salles, terrains et amphis du campus, proposes pendant qu'on ecrit.

    Ecrire le nom du lieu suffit : le placer sur le plan n'est plus
    demande, l'evenement n'aura simplement pas de point sur la carte. Mais
    choisir un lieu propose lui donne sa position d'un coup, comme une
    adresse de l'annuaire en ville.
  */
  const [lieuxCampus, setLieuxCampus] = useState<LieuDuCampus[]>([]);
  useEffect(() => {
    if (!campus || !server) return;
    /* Ceux de la maquette, puis ceux que d'autres ont deja ecrits : la liste se complete a l'usage. */
    void Promise.all([
      lieuxDuCampus(server.id).catch(() => []),
      events.lieuxDejaDonnes().catch(() => []),
    ]).then(([connus, donnes]) => setLieuxCampus(avecLesLieuxDonnes(connus, donnes)));
    // `campus` ne change pas en cours de route : c'est le spot courant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [server?.id]);
  const dateId = useId();
  const timesId = useId();
  const placeId = useId();

  // Les champs natifs de date et d'heure cachent leur declencheur dans un
  // coin minuscule : on garde une reference pour ouvrir le selecteur des
  // qu'on touche la boite, et pas seulement la petite icone du navigateur.
  const dateRef = useRef<HTMLInputElement>(null);
  const startRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<EventCategory>(propose?.categorie ?? 'sport');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  /*
    Le lieu n'est plus un texte libre.

    « Chez moi », « le parc », une faute de frappe : celui qui s'y rend
    n'avait rien a ouvrir, et l'organisateur ne le savait pas. On demande
    donc une adresse que quelqu'un d'autre puisse retrouver, choisie dans
    l'annuaire — et l'on garde son point avec elle, pour mener au bon
    endroit quel que soit le nom qu'on lui donne.
  */
  const [location, setLocation] = useState(propose?.libelle ?? '');
  const [point, setPoint] = useState<{ lat: number; lon: number } | null>(
    propose ? { lat: propose.lat, lon: propose.lon } : null,
  );
  const [pistes, setPistes] = useState<Adresse[]>([]);
  const [cherche, setCherche] = useState(false);
  /* L'annuaire n'a pas repondu : ce n'est pas « aucune adresse ». */
  const [muet, setMuet] = useState(false);

  /*
    On interroge l'annuaire quand la frappe s'arrete, pas pendant.

    Nominatim demande une requete par seconde au plus, et une adresse
    ne se reconnait pas a trois lettres de toute facon. L'attente
    repart a chaque touche : seule la derniere version de la question
    part.
  */
  useEffect(() => {
    if (point || campus) return;
    const mot = location.trim();
    if (mot.length < 3) {
      setPistes([]);
      return;
    }
    const abandon = new AbortController();
    const minuteur = window.setTimeout(() => {
      setCherche(true);
      void chercherUneAdresse(mot, abandon.signal, {
        limites: server?.limites,
        pres: server?.centre,
      })
        .then((trouvees) => {
          setPistes(trouvees);
          setMuet(false);
        })
        .catch((cause) => {
          /* Un abandon n'est pas une panne : c'est nous qui l'avons
             demande en repartant sur une frappe plus recente. */
          if (cause instanceof DOMException && cause.name === 'AbortError') return;
          setPistes([]);
          setMuet(true);
        })
        .finally(() => setCherche(false));
    }, 450);
    return () => {
      abandon.abort();
      window.clearTimeout(minuteur);
    };
    // `campus` ne change pas en cours de route : c'est le spot courant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, point]);
  const [description, setDescription] = useState('');
  const [maxParticipants, setMaxParticipants] = useState(10);
  const [requiresValidation, setRequiresValidation] = useState(true);
  /*
    La ou les langues de la sortie, deja reglees sur celle de l'organisateur.

    C'est la qu'une ville a plusieurs langues se tient : pas en la scindant,
    ce qui diviserait le monde qu'il faut reunir, mais en disant a chaque
    sortie ce qu'on y parlera. Un fil un peu melange se lit ; une soiree ou
    l'on ne se comprend pas est une soiree ratee.
  */
  const [langues, setLangues] = useState<LangueParlee[]>([langue]);
  /*
    La sortie spontanee.

    Le formulaire complet est juste pour un atelier et absurde pour « je
    vais au cafe Clock a 18 h, qui vient ? ». Sur une ville presque vide,
    le goulot n'est pas la consommation d'evenements, c'est l'offre : il
    faut que proposer coute trois champs, pas neuf.
  */
  const [type, setType] = useState<'evenement' | 'spontanee' | 'enLigne'>('evenement');
  const spontane = type === 'spontanee';
  /*
    L'evenement en ligne : revisions en visio, reunion d'asso, tournoi
    depuis chez soi. Le formulaire d'un evenement, avec un lien a la place
    de l'adresse.
  */
  const enLigne = type === 'enLigne';
  const [lien, setLien] = useState('');
  const lienId = useId();
  /*
    L'affiche, facultative.

    Sans elle, la carte prend l'image de la categorie : une sortie sans
    affiche ne doit pas etre un rectangle vide a cote de celles qui en ont
    une. C'est aussi pourquoi la categorie reste obligatoire.
  */
  const [affiche, setAffiche] = useState<string | null>(null);
  /* A partir de combien de monde la sortie se confirme. Zero = sans seuil. */
  const [seuil, setSeuil] = useState(0);

  const [error, setError] = useState<string | null>(null);
  const [field, setField] = useState<string | null>(null);
  /* Un lieu choisi, ou ecrit exactement comme l'un d'eux, n'a plus besoin de la liste. */
  const lieuxTrouves =
    campus && !lieuxCampus.some((lieu) => lieu.libelle === location.trim())
      ? chercherSurLeCampus(lieuxCampus, location)
      : [];
  const [pending, setPending] = useState(false);

  async function onPublish() {
    setError(null);
    setField(null);
    setPending(true);
    const input: CreateEventInput = {
      title,
      category,
      date,
      startTime,
      endTime,
      location: enLigne ? '' : location,
      lat: enLigne ? null : point?.lat ?? null,
      lon: enLigne ? null : point?.lon ?? null,
      description,
      langues,
      maxParticipants,
      requiresValidation,
      seuil: spontane ? 0 : seuil,
      spontane,
      ...(enLigne ? { enLigne: true, lien } : {}),
      ...(affiche ? { imageUrl: affiche } : {}),
      ...(asso ? { asso } : {}),
    };
    try {
      await events.create(input);
      /* Depuis une association, on revient a sa page. */
      if (asso) {
        navigate(-1);
        return;
      }
      navigate('/app/events');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.sorties.creation.impossible);
      if (cause instanceof SiroccoApiError && cause.field) setField(cause.field);
    } finally {
      setPending(false);
    }
  }

  return (
    <SubScreen
      title={spontane ? t.sorties.creation.titreSortie : t.sorties.creation.titreEvenement}
      fromTop
      overlay={
        placer && campus && server && maquetteExiste(server.id) ? (
          <Suspense fallback={null}>
            <PlacerSurLaMaquette
              maquette={server.id}
              nuit={ambiance === 'night'}
              point={point}
              onFermer={() => setPlacer(false)}
              onValider={(choisi) => {
                setPoint(choisi);
                setPlacer(false);
              }}
            />
          </Suspense>
        ) : placer && campus ? (
          <Suspense fallback={null}>
            <PlacerSurLeCampus
              centre={campus}
              etendue={server?.etendueCarte ?? 0.012}
              nom={server?.name}
              nuit={ambiance === 'night'}
              point={point}
              onFermer={() => setPlacer(false)}
              onValider={(choisi) => {
                setPoint(choisi);
                setPlacer(false);
              }}
            />
          </Suspense>
        ) : null
      }
      bottomBar={
        <Button block loading={pending} onClick={onPublish}>
          {spontane ? t.sorties.creation.publierSortie : t.sorties.creation.publierEvenement}
        </Button>
      }
    >
      <div className="ev-form">
        {nomDeLAsso && (
          <p className="ev-form__asso">
            <UsersThree size={18} />
            {t.messages.annuaire.associations.pourLAsso(nomDeLAsso)}
          </p>
        )}
        {/*
          Le choix se fait en tete, parce qu'il change tout ce qui suit.
          Deux mots plutot qu'un interrupteur : « spontanee » ne se devine
          pas, et l'explication tient sous les onglets. A trois, les mots
          raccourcissent : « Sortie spontanee » ne tenait plus dans son tiers.
        */}
        <div className="field field--spot">
          <div className="ev-pills" role="radiogroup" aria-label={t.sorties.creation.type}>
            {(['evenement', 'spontanee', 'enLigne'] as const).map((valeur) => (
              <button
                key={valeur}
                type="button"
                role="radio"
                aria-checked={type === valeur}
                className={`ev-pill${type === valeur ? ' ev-pill--on' : ''}`}
                onClick={() => setType(valeur)}
              >
                {valeur === 'evenement'
                  ? t.sorties.creation.evenement
                  : valeur === 'enLigne'
                    ? t.sorties.creation.enLigne
                    : t.sorties.creation.spontanee}
              </button>
            ))}
          </div>
        </div>

        {/*
          L'affiche en tete du formulaire : c'est la premiere chose qu'on
          verra de l'evenement, autant que ce soit la premiere qu'on
          choisisse. Un cadre carre, centre, sans un mot — le geste se
          devine, et deux phrases d'explication pesaient plus lourd que
          ce qu'elles expliquaient.
        */}
        <div className="field field--spot field--centre">
          <span className="field__label">{t.sorties.creation.affiche}</span>
          <div className="ev-affiche">
            <PhotoPicker
              preset="photo"
              label={t.sorties.creation.choisirAffiche}
              className="ev-affiche__choix"
              onPicked={setAffiche}
              onError={(message) => setError(message)}
            >
              {affiche ? (
                <img src={affiche} alt="" className="ev-affiche__vue" />
              ) : (
                <ImageSquare size={34} />
              )}
            </PhotoPicker>
            {affiche && (
              <button
                type="button"
                className="ev-affiche__retirer"
                aria-label={t.sorties.creation.retirerAffiche}
                onClick={() => setAffiche(null)}
              >
                <X size={15} weight="bold" />
              </button>
            )}
          </div>
        </div>

        <Field
          label={spontane ? t.sorties.creation.quoi : t.sorties.creation.titre}
          tone="spot"
          placeholder={spontane ? t.sorties.creation.exempleSortie : t.sorties.creation.exempleEvenement}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          error={field === 'title' ? error : null}
        />

        {/* Sous le titre : les deux se lisent ensemble, drapeaux compris. */}
        <ChampDesLangues
          label={t.sorties.creation.langues}
          valeur={langues}
          onChange={setLangues}
        />

        {!spontane && (
        <div className="field field--spot">
          <span className="field__label">{t.sorties.creation.categorie}</span>
          <div className="ev-pills ev-pills--grille" role="radiogroup" aria-label={t.sorties.creation.categorie}>
            {categories.map((value) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={value === category}
                className={`ev-pill${value === category ? ' ev-pill--on' : ''}`}
                onClick={() => setCategory(value)}
              >
                {t.sorties.categories[value]}
              </button>
            ))}
          </div>
        </div>
        )}

        {/*
          La date occupe toute la largeur : coincee dans une demi-colonne,
          la zone tactile du calendrier natif devenait trop etroite pour
          etre visee du pouce.
        */}
        <div className="field field--spot">
          <label className="field__label" htmlFor={dateId}>
            {t.sorties.commun.date}
          </label>
          <div
            className={`ev-box ev-box--picker${field === 'date' ? ' ev-box--error' : ''}`}
            onClick={() => openPicker(dateRef.current)}
          >
            <Calendar size={18} />
            <input
              id={dateId}
              ref={dateRef}
              type="date"
              // On n'organise pas un evenement hier.
              min={todayIso()}
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
          </div>
          {field === 'date' && <p className="field__error">{error}</p>}
        </div>

        <div className="field field--spot">
          <label className="field__label" htmlFor={timesId}>
            {t.sorties.creation.horaires}
          </label>
          {/* Champs d'heure natifs : plus de "h" a taper a la main. */}
          <div
            className={`ev-box ev-times${
              field === 'startTime' || field === 'endTime' ? ' ev-box--error' : ''
            }`}
          >
            <Clock size={18} />
            <input
              id={timesId}
              ref={startRef}
              type="time"
              aria-label={t.sorties.creation.heureDebut}
              value={startTime}
              onClick={() => openPicker(startRef.current)}
              onChange={(event) => setStartTime(event.target.value)}
            />
            <span className="ev-times__dash" aria-hidden="true">
              –
            </span>
            <input
              ref={endRef}
              type="time"
              aria-label={t.sorties.creation.heureFin}
              value={endTime}
              onClick={() => openPicker(endRef.current)}
              onChange={(event) => setEndTime(event.target.value)}
            />
          </div>
          {(field === 'startTime' || field === 'endTime') && (
            <p className="field__error">{error}</p>
          )}
        </div>

        {/*
          En ligne, le lien a la place du lieu. Le clavier est celui des
          adresses web, sans majuscule ni correction : un lien corrige ne
          mene plus nulle part.
        */}
        {enLigne ? (
        <div className="field field--spot">
          <label className="field__label" htmlFor={lienId}>
            {t.sorties.creation.lien}
          </label>
          <div className={`ev-box${field === 'lien' ? ' ev-box--error' : ''}`}>
            <LinkSimple size={18} />
            <input
              id={lienId}
              type="url"
              inputMode="url"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder={t.sorties.creation.lienExemple}
              value={lien}
              onChange={(event) => setLien(event.target.value)}
            />
          </div>
          {field === 'lien' ? (
            <p className="field__error">{error}</p>
          ) : (
            <p className="field__hint">{t.sorties.creation.lienAide}</p>
          )}
        </div>
        ) : (
        <div className="field field--spot">
          <label className="field__label" htmlFor={placeId}>
            {t.sorties.commun.lieu}
          </label>

          <div className={`ev-box${field === 'location' ? ' ev-box--error' : ''}`}>
            <MapPin size={18} />
            <input
              id={placeId}
              type="text"
              placeholder={campus ? t.sorties.creation.ouSurLeCampus : t.sorties.creation.chercheAdresse}
              value={location}
              onChange={(event) => {
                setLocation(event.target.value);
                /* Retoucher l'adresse defait le choix : le point ne vaut
                   que pour ce qui a ete choisi, pas pour ce qu'on a
                   retape par-dessus. Sur un campus, le point vient du
                   plan : renommer le lieu ne le deplace pas. */
                if (!campus) setPoint(null);
              }}
            />
            {point && <Check size={18} weight="bold" className="ev-lieu__valide" />}
          </div>

          {/* Sur le campus, ses lieux connus : en choisir un le place sur la carte. */}
          {campus && !point && lieuxTrouves.length > 0 && (
            <ul className="ev-adresses">
              {lieuxTrouves.map((lieu) => (
                <li key={lieu.libelle}>
                  <button
                    type="button"
                    className="ev-adresses__piste"
                    onClick={() => {
                      setLocation(lieu.libelle);
                      /* Un lieu sans position se nomme sans se placer : il ne sera pas sur la carte. */
                      if (lieu.lat !== null && lieu.lon !== null) setPoint({ lat: lieu.lat, lon: lieu.lon });
                    }}
                  >
                    <MapPin size={16} />
                    <span className="ev-adresses__nom">{lieu.nom}</span>
                    {lieu.batiment && <span className="ev-adresses__detail">{lieu.batiment}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Les propositions de l'annuaire. Il faut en choisir une : c'est
              ce qui garantit qu'on pourra s'y rendre. */}
          {/* Sur un campus, le plan reste la pour placer un lieu que la liste ne connait pas. */}
          {campus && (
            <button
              type="button"
              className={`ev-placer-bouton${point ? ' ev-placer-bouton--pose' : ''}`}
              onClick={() => setPlacer(true)}
            >
              <MapTrifold size={18} />
              {point ? t.sorties.creation.placeSurLePlan : t.sorties.creation.placerSurLePlanBouton}
            </button>
          )}

          {!campus && !point && pistes.length > 0 && (
            <ul className="ev-adresses">
              {pistes.map((piste) => (
                <li key={`${piste.lat},${piste.lon}`}>
                  <button
                    type="button"
                    className="ev-adresses__piste"
                    onClick={() => {
                      setLocation(piste.libelle);
                      setPoint({ lat: piste.lat, lon: piste.lon });
                      setPistes([]);
                    }}
                  >
                    <MapPin size={16} />
                    {piste.libelle}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {!campus && !point && !cherche && location.trim().length >= 3 && pistes.length === 0 && (
            <p className="field__hint">
              {muet
                ? t.sorties.creation.annuaireMuet
                : t.sorties.creation.aucuneAdresse}
            </p>
          )}
          {field === 'location' && <p className="field__error">{error}</p>}
        </div>
        )}

        {!spontane && (
        <div className="field field--spot">
          <span className="field__label">{t.sorties.creation.nombreParticipants}</span>
          <div className="card ev-stepper-card">
            <span className="ev-stepper-card__label">{t.sorties.creation.maximum}</span>
            <div className="ev-stepper">
              <button
                type="button"
                className="ev-step ev-step--minus"
                aria-label={t.sorties.creation.retirerPlace}
                disabled={maxParticipants <= MIN_PARTICIPANTS}
                onClick={() => setMaxParticipants((value) => value - 1)}
              >
                −
              </button>
              <span className="ev-stepper__value" aria-live="polite">
                {maxParticipants}
              </span>
              <button
                type="button"
                className="ev-step ev-step--plus"
                aria-label={t.sorties.creation.ajouterPlace}
                onClick={() => setMaxParticipants((value) => value + 1)}
              >
                +
              </button>
            </div>
          </div>
          {/*
            Le seuil de declenchement.

            Sur une ville presque vide, personne ne veut etre le premier
            inscrit d'un evenement qui fera peut-etre deux personnes.
            Annoncer « ca se fait a partir de quatre » retire ce risque :
            s'inscrire n'engage plus a rien tant que le compte n'y est pas.
          */}
          <div className="card ev-stepper-card">
            <span className="ev-stepper-card__label">{t.sorties.creation.seConfirme}</span>
            <div className="ev-stepper">
              <button
                type="button"
                className="ev-step ev-step--minus"
                aria-label={t.sorties.creation.abaisserSeuil}
                disabled={seuil <= 0}
                onClick={() => setSeuil((value) => (value <= 2 ? 0 : value - 1))}
              >
                −
              </button>
              <span className="ev-stepper__value" aria-live="polite">
                {seuil > 1 ? seuil : t.sorties.creation.aucun}
              </span>
              <button
                type="button"
                className="ev-step ev-step--plus"
                aria-label={t.sorties.creation.releverSeuil}
                disabled={seuil >= maxParticipants}
                onClick={() => setSeuil((value) => (value < 2 ? 2 : value + 1))}
              >
                +
              </button>
            </div>
          </div>
          {field === 'maxParticipants' && <p className="field__error">{error}</p>}
        </div>
        )}

        {!spontane && (
        <TextArea
          label={t.sorties.creation.description}
          tone="spot"
          placeholder={t.sorties.creation.descriptionExemple}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          error={field === 'description' ? error : null}
        />
        )}

        {!spontane && (
        <>
        {/*
          Une seule carte, sans filet interieur : les deux reglages se
          repondent, les separer les ferait lire comme deux blocs.
        */}
        <div className="card ev-switches">
          <div className="ev-switch-row">
            <span>{t.sorties.creation.validation}</span>
            <Toggle
              checked={requiresValidation}
              onChange={setRequiresValidation}
              label={t.sorties.creation.validation}
            />
          </div>
        </div>
        </>
        )}

        {/* Erreur qui ne vise aucun champ : spot manquant, reseau coupe... */}
        {error && !field && <p className="field__error">{error}</p>}
      </div>
    </SubScreen>
  );
}
